import { useState, useEffect, useCallback } from 'react';
import { Save, Search, RefreshCw, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTranslations() {
  const { t, refreshTranslations } = useLanguage();
  const { token } = useAuth();
  const [translations, setTranslations] = useState({ el: {}, en: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const fetchTranslations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/translations`);
      if (res.ok) {
        const data = await res.json();
        setTranslations(data);
      }
    } catch {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/translations`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(translations)
      });

      if (res.ok) {
        toast.success(t('translationsSaved') || 'Translations saved');
        setHasChanges(false);
        // Refresh translations in the context
        if (refreshTranslations) {
          await refreshTranslations();
        }
      } else {
        throw new Error('Failed to save');
      }
    } catch {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleTranslationChange = (lang, key, value) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  // Get all unique keys from both languages
  const allKeys = [...new Set([
    ...Object.keys(translations.el || {}),
    ...Object.keys(translations.en || {})
  ])].sort();

  // Filter keys based on search
  const filteredKeys = allKeys.filter(key => {
    const searchLower = searchTerm.toLowerCase();
    return (
      key.toLowerCase().includes(searchLower) ||
      (translations.el[key] || '').toLowerCase().includes(searchLower) ||
      (translations.en[key] || '').toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="translations-loading">
        <div className="animate-spin h-8 w-8 border-4 border-muted border-t-primary rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="admin-translations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Globe className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {t('translations') || 'Translations'}
            </h1>
            <p className="text-sm text-muted-foreground">
              Edit Greek and English translations
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTranslations}
            disabled={loading}
            data-testid="refresh-translations-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            size="sm"
            data-testid="save-translations-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : t('save')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search translations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-translations-input"
        />
      </div>

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-600 dark:text-amber-400 text-sm">
          You have unsaved changes. Click "Save" to apply them.
        </div>
      )}

      {/* Translations Table */}
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        {/* Header */}
        <div className="grid grid-cols-12 gap-2 p-3 bg-muted/50 border-b border-border text-sm font-medium text-muted-foreground">
          <div className="col-span-3">Key</div>
          <div className="col-span-4 md:col-span-5 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-5 rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold">EL</span>
            Greek
          </div>
          <div className="col-span-5 md:col-span-4 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-5 rounded bg-green-500/20 text-green-600 dark:text-green-400 text-xs font-bold">EN</span>
            English
          </div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
          {filteredKeys.map((key) => (
            <div 
              key={key} 
              className="grid grid-cols-12 gap-2 p-3 hover:bg-muted/30 transition-colors"
              data-testid={`translation-row-${key}`}
            >
              <div className="col-span-3 text-sm font-mono text-muted-foreground break-all pt-2">
                {key}
              </div>
              <div className="col-span-4 md:col-span-5">
                <Input
                  value={translations.el[key] || ''}
                  onChange={(e) => handleTranslationChange('el', key, e.target.value)}
                  placeholder="Greek translation"
                  className="text-sm"
                  data-testid={`translation-el-${key}`}
                />
              </div>
              <div className="col-span-5 md:col-span-4">
                <Input
                  value={translations.en[key] || ''}
                  onChange={(e) => handleTranslationChange('en', key, e.target.value)}
                  placeholder="English translation"
                  className="text-sm"
                  data-testid={`translation-en-${key}`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {filteredKeys.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            No translations found matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="mt-4 text-sm text-muted-foreground text-center">
        Showing {filteredKeys.length} of {allKeys.length} translation keys
      </div>
    </div>
  );
}
